import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../CommonTable/CommonTable.scss";
import "./ViewFileUploaded.scss";
import {
  Pagination,
  PaginationItem,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { LightButton } from "../../AllButtons/AllButtons";
import { useAppDispatch } from "../../../Store/store";
import { getKnowledgeBaseUploadEntries } from "../../../Services/admin.service";
import { openSnackbar } from "../../../Slice/snackbarSlice";
import { getErrorMessage } from "../../../Utils/errorMessage";

interface UploadedDocument {
  upload_id?: number;
  file_name?: string;
  total_rows?: number;
  imported_rows?: number;
  invalid_rows?: number;
  duplicate_rows?: number;
  created_at?: string;
}

const ViewFileUploaded = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { upload_id } = useParams();
  const rowsPerPage = 20;

  const [isLoading, setIsLoading] = useState(true);
  const [documentData, setDocumentData] = useState<UploadedDocument | null>(null);
  const [entries, setEntries] = useState<Record<string, any>[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const formatColumnName = (value: string) => {
    return value
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const formatCellValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };

  const formatDateValue = (value: any) => {
    if (!value) {
      return "-";
    }

    const dateValue = new Date(value);

    if (Number.isNaN(dateValue.getTime())) {
      return String(value);
    }

    return dateValue.toLocaleString();
  };

  const summaryCards = [
    {
      label: "Total Rows",
      value: formatCellValue(documentData?.total_rows),
    },
    {
      label: "Uploaded Rows",
      value: formatCellValue(documentData?.imported_rows),
    },
    {
      label: "Invalid Rows",
      value: formatCellValue(documentData?.invalid_rows),
    },
    {
      label: "Duplicate Rows",
      value: formatCellValue(documentData?.duplicate_rows),
    },
    {
      label: "Uploaded On",
      value: formatDateValue(documentData?.created_at),
    },
  ];

  const getArrayValue = useCallback((value: any) => {
    return Array.isArray(value) ? value : [];
  }, []);

  const getDocumentFromResponse = useCallback((response: any, uploadId: number) => {
    const documentValue = response?.document ?? response?.data?.document;
    const documents = getArrayValue(documentValue);
    const matchedDocument = documents.find((documentItem) => Number(documentItem?.upload_id) === uploadId);

    if (matchedDocument) {
      return matchedDocument;
    }

    if (documentValue && !Array.isArray(documentValue)) {
      return documentValue;
    }

    return documents[0] ?? null;
  }, [getArrayValue]);

  const getEntriesFromResponse = useCallback((response: any, uploadId: number) => {
    const documentDataByUploadId = getDocumentFromResponse(response, uploadId);
    const possibleEntries =
      documentDataByUploadId?.entries ??
      documentDataByUploadId?.records ??
      documentDataByUploadId?.rows ??
      documentDataByUploadId?.items ??
      response?.entries ??
      response?.data?.entries ??
      response?.records ??
      response?.rows ??
      response?.items ??
      response?.data?.records ??
      response?.data?.rows ??
      response?.data?.items;

    const formattedEntries = getArrayValue(possibleEntries).map((entry) => (
      entry && typeof entry === "object" && !Array.isArray(entry) ? entry : { data: entry }
    ));
    const hasUploadIdField = formattedEntries.some((entry) => entry?.upload_id !== undefined || entry?.uploadId !== undefined);

    return hasUploadIdField
      ? formattedEntries.filter((entry) => Number(entry?.upload_id ?? entry?.uploadId) === uploadId)
      : formattedEntries;
  }, [getArrayValue, getDocumentFromResponse]);

  const getPaginationFromResponse = (response: any, requestedPage: number) => {
    const payload = response?.data ?? response;
    const currentPageValue = Number(payload?.current_page);
    const totalPagesValue = Number(payload?.total_pages);
    const totalCountValue = Number(payload?.total_count);

    const resolvedCurrentPage = currentPageValue > 0 ? currentPageValue : requestedPage;
    const resolvedTotalPages =
      totalPagesValue > 0
        ? totalPagesValue
        : totalCountValue > 0
          ? Math.ceil(totalCountValue / rowsPerPage)
          : 1;

    return {
      currentPage: resolvedCurrentPage,
      totalPages: Math.max(resolvedTotalPages, 1),
    };
  };

  const columns = useMemo(() => {
    const columnSet = new Set<string>();

    entries.forEach((entry) => {
      Object.keys(entry || {}).forEach((key) => columnSet.add(key));
    });

    return Array.from(columnSet);
  }, [entries]);

  const fetchUploadedFileEntries = useCallback(async (pageNumber: number = 1) => {
    const numericUploadId = Number(upload_id);

    if (!numericUploadId) {
      setIsLoading(false);
      setCurrentPage(1);
      setTotalPages(1);
      dispatch(
        openSnackbar({
          message: "Invalid upload id.",
          severity: "error",
        })
      );
      return;
    }

    setIsLoading(true);
    try {
      const response = await dispatch(
        getKnowledgeBaseUploadEntries({
          uploadId: numericUploadId,
          users_per_page: rowsPerPage,
          page_number: pageNumber,
        })
      ).unwrap();
      const pagination = getPaginationFromResponse(response, pageNumber);

      setDocumentData(getDocumentFromResponse(response, numericUploadId));
      setEntries(getEntriesFromResponse(response, numericUploadId));
      setCurrentPage(pagination.currentPage);
      setTotalPages(pagination.totalPages);
    } catch (error: any) {
      setDocumentData(null);
      setEntries([]);
      setCurrentPage(1);
      setTotalPages(1);
      dispatch(
        openSnackbar({
          message: getErrorMessage(error, "Failed to fetch uploaded file entries."),
          severity: "error",
        })
      );
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, getDocumentFromResponse, getEntriesFromResponse, upload_id]);

  useEffect(() => {
    setCurrentPage(1);
    setTotalPages(1);
    fetchUploadedFileEntries(1);
  }, [fetchUploadedFileEntries, upload_id]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    if (page === currentPage || isLoading) {
      return;
    }

    fetchUploadedFileEntries(page);
  };

  return (
    <div className="admin-content-wrapper view-file-uploaded-page">
      <div className="admin-content-subheader">
        <div className="admin-content-subheader-title">View File Uploaded</div>
        <LightButton onClick={() => navigate("/admin/knowledge_base")}>Back</LightButton>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "16px",
          marginBottom: "16px",
        }}
      >
        {summaryCards.map((card) => (
          <div className="common-card-wrap" key={card.label}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#525866", marginBottom: "12px" }}>
              {card.label}
            </div>
            <div style={{ fontSize: "14px", fontWeight: 500, color: "#374151" }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      <div className="common-table-wrapper">
        <div className="user-management-table">
          <div className="common-table-filter-block">
            <div style={{ marginRight: "auto" }}>
              {documentData?.file_name || "Uploaded File"}
            </div>
          </div>

          <TableContainer className="view-file-uploaded-table-container">
            <Table stickyHeader aria-label="view uploaded file table">
              <TableHead>
                <TableRow>
                  {columns.length > 0 ? (
                    columns.map((column) => (
                      <TableCell key={column}>{formatColumnName(column)}</TableCell>
                    ))
                  ) : (
                    <TableCell>Data</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(columns.length, 1)} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : entries.length > 0 ? (
                  entries.map((entry, rowIndex) => (
                    <TableRow key={String(entry?.id ?? entry?.entry_id ?? rowIndex)} hover>
                      {columns.map((column) => (
                        <TableCell key={`${rowIndex}-${column}`}>
                          {formatCellValue(entry?.[column])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={Math.max(columns.length, 1)} align="center">
                      No file data found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!isLoading && totalPages > 1 && (
            <div className="common-table-pagination">
              <div></div>
              <Stack spacing={2}>
                <Pagination
                  variant="outlined"
                  shape="rounded"
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  renderItem={(item) => (
                    <PaginationItem
                      {...item}
                      components={{
                        previous: ArrowBack,
                        next: ArrowForward,
                      }}
                    />
                  )}
                />
              </Stack>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewFileUploaded;
